import React, { FC, useRef, useState } from 'react'
import * as Yup from 'yup'

import Form, { FormGeneratorHandles } from '@/components/Form'
import { FilePreview, FormItemProps } from '@/components/Form/types'
import { Button } from '@/components/UiElements'
import { startNotif, closeNotif, updateNotif } from '@/components/DynamicForm/util/notifier'
import GlbComponent from '@/components/UiElements/GlbComponent'
import upload from '@/services/upload'
import saveViewProfile from '@/services/ProductService/saveViewProfile'
import renderGlbThumbnail from '@/utils/renderGlbThumbnail'
import Splash from '@/components/splash/Splash'

interface ViewProfileFormProps {
  profile: any | null
  onUpdateSuccess: (created?: { id?: any; name?: string }) => void
  defaultSku?: string
  onCancel?: () => void
}

const formItems: Array<FormItemProps> = [
  {
    name: 'name',
    label: 'Profile Name',
    type: 'Text',
    classNames: { container: 'col-span-12' },
    validation: Yup.string().required('Profile name is required'),
  },
  {
    name: 'sku',
    label: 'SKU',
    type: 'Text',
    classNames: { container: 'col-span-12' },
    validation: Yup.string().required('SKU is required'),
  },
  {
    name: 'glb',
    label: '3D Model File (.glb / .gltf)',
    type: 'File',
    classNames: {
      container: 'col-span-12',
      content: '!h-[83px]',
    },
    placeholder: 'Drag and drop your 3D model here',
    itemOptions: {
      filePreview: (file: FilePreview) =>
        file.string ? (
          <GlbComponent src={file.string} style={{ height: '70px', width: '70px' }} className="w-[70px] h-[70px]" />
        ) : null,
      acceptFormat: '.glb, .gltf, model/gltf-binary, model/gltf+json',
      download: true,
    },
    defaultValue: null,
  },
  {
    name: 'sectionModel',
    label: 'Section Placement',
    help: 'How customizable sections are positioned on this model. Cylindrical is the long-standing behaviour. Frames place each section by where it sits and which way it faces, which is the only option that works on shapes that are not round.',
    type: 'Select',
    itemOptions: {
      list: [
        { value: 'cylindrical', label: 'Cylindrical (angle around the model)' },
        { value: 'frame', label: 'Frames (place anywhere on the surface)' },
      ],
    },
    // Defaults to the existing behaviour on purpose. A new profile must never land on the
    // new path by accident — that is exactly how 98 working cylindrical products would
    // start rendering through untested maths.
    defaultValue: { value: 'cylindrical', label: 'Cylindrical (angle around the model)' },
    classNames: { container: 'col-span-12' },
  },
  {
    name: 'uvProjection',
    label: 'UV Projection',
    type: 'Select',
    itemOptions: {
      list: [
        { value: 'planar', label: 'Planar (best for flat objects)' },
        { value: 'cylindrical', label: 'Cylindrical (best for cups/bottles)' },
        { value: 'box', label: 'Box (best for general shapes)' },
        { value: 'none', label: 'None (keep original UVs for GLB)' },
      ],
    },
    defaultValue: { value: 'none', label: 'None (keep original UVs for GLB)' },
    classNames: { container: 'col-span-12' },
  },
]

const ViewProfileForm: FC<ViewProfileFormProps> = ({ profile, onUpdateSuccess, defaultSku, onCancel }) => {
  const formRef = useRef<FormGeneratorHandles>(null)
  const [loading, setLoading] = useState<boolean>(false)

  const isEditing = profile?.id && profile.id !== 'duplicate'
  const isDuplicate = profile?.id === 'duplicate'

  const sectionModelLabels: Record<string, string> = {
    cylindrical: 'Cylindrical (angle around the model)',
    frame: 'Frames (place anywhere on the surface)',
  }

  const uvProjectionLabels: Record<string, string> = {
    planar: 'Planar (best for flat objects)',
    cylindrical: 'Cylindrical (best for cups/bottles)',
    box: 'Box (best for general shapes)',
    none: 'None (keep original UVs for GLB)',
  }

  const defaultValues: any = {}
  if (defaultSku) {
    defaultValues.sku = defaultSku
  }
  if (isEditing || isDuplicate) {
    defaultValues.name = isDuplicate ? `${profile.name} (copy)` : profile.name
    defaultValues.sku = profile?.data?.sku || defaultSku || ''
    const uvVal = profile?.data?.uv_projection || 'none'
    defaultValues.uvProjection = { value: uvVal, label: uvProjectionLabels[uvVal] || uvVal }
    // Absent means a profile that predates the field, and every consumer has always read
    // a missing projection as cylindrical — so that is what it keeps meaning here.
    const smVal = profile?.data?.section_model || 'cylindrical'
    defaultValues.sectionModel = { value: smVal, label: sectionModelLabels[smVal] || smVal }
  }

  // Load existing GLB URL after form mounts
  React.useEffect(() => {
    const viewUrl = profile?.data?.view_url
    if (viewUrl && (isEditing || isDuplicate)) {
      formRef.current?.changeInput('glb', viewUrl)
    }
  }, [profile])

  const handleSave = async () => {
    const errors = await formRef.current?.validate()
    if (errors) return

    setLoading(true)
    const notifId = startNotif('Saving view profile...')
    const data = formRef.current?.getData()

    if (!data) {
      closeNotif(notifId, 'Validation failed', { type: 'error' })
      setLoading(false)
      return
    }

    try {
      let viewUrl: string | null = null

      // Upload GLB file
      if (data.glb?.data) {
        updateNotif(notifId, 'Uploading GLB file...')
        const versionByDate = new Date().toISOString().replace(/[:.]/g, '-').replace('T', '_').substring(0, 19)
        const fileName = `(${data.sku})_${data.name}_${versionByDate}.glb`
          .toLowerCase()
          .replace(/-/g, '')
          .replace(/ /g, '-')
        viewUrl = await upload({ file: data.glb.data, fileName, folderName: 'glb_files' })
      } else if (data.glb?.url) {
        viewUrl = data.glb.url
      }
      if (!viewUrl) {
        closeNotif(notifId, 'Please upload a 3D model file', { type: 'error' })
        setLoading(false)
        return
      }

      // Generate and upload model thumbnail
      let thumbnailUrl: string | null = null
      try {
        updateNotif(notifId, 'Generating thumbnail...')
        const thumbDataUrl = await renderGlbThumbnail(viewUrl)
        const thumbVersionByDate = new Date().toISOString().replace(/[:.]/g, '-').replace('T', '_').substring(0, 19)
        const thumbFileName = `(${data.sku})_${data.name}_${thumbVersionByDate}__thumb.png`
          .toLowerCase()
          .replace(/-/g, '')
          .replace(/ /g, '-')
        thumbnailUrl = await upload({
          file: thumbDataUrl,
          fileName: thumbFileName,
          folderName: 'view_profile_thumbnails',
        })
      } catch {
        // Non-blocking — list will fall back to GlbComponent if thumbnail is missing
        console.warn('Failed to generate model thumbnail')
      }

      const payload: any = {
        name: data.name,
        data: {
          view_url: viewUrl,
          sku: data.sku,
          uv_projection: data.uvProjection?.value || 'none',
          section_model: data.sectionModel?.value || 'cylindrical',
          ...(thumbnailUrl ? { model_thumbnail_image: thumbnailUrl } : {}),
        },
      }

      if (isEditing) {
        payload.id = profile.id
      }

      updateNotif(notifId, 'Saving view profile...')
      const result = await saveViewProfile(payload)
      if (result) {
        closeNotif(notifId, 'View profile saved successfully')
        setLoading(false)
        // Surface the created profile so the caller can auto-select it in a dropdown
        onUpdateSuccess({ id: (result as any)?.data?.id ?? profile?.id, name: payload.name })
        return
      } else {
        closeNotif(notifId, 'Failed to save view profile', { type: 'error' })
        setLoading(false)
        return
      }
    } catch {
      closeNotif(notifId, 'Failed to save view profile', { type: 'error' })
      setLoading(false)
      return
    }
  }

  return (
    <div className="flex flex-col gap-xxxl">
      {loading && <Splash />}
      <div className="w-[550px] max-w-[calc(100vw-80px)] grid grid-cols-12 gap-md">
        <Form ref={formRef} items={formItems} defaultValues={defaultValues} />
      </div>
      <div className="flex justify-end gap-md">
        {onCancel && (
          <Button size="lg" type="alt" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
        )}
        <Button size="lg" onClick={handleSave} disabled={loading}>
          {isEditing ? 'Update View Profile' : 'Add View Profile'}
        </Button>
      </div>
    </div>
  )
}

export default ViewProfileForm
